import React, { useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Seo } from 'src/components/seo';
import SvgIcon from '@mui/material/SvgIcon';
import Container from '@mui/material/Container';
import { CirclesWithBar } from 'react-loader-spinner';
import { Grid, TextField } from '@mui/material';
import SearchMdIcon from '@untitled-ui/icons-react/build/esm/SearchMd';
import { PricingPlan } from 'src/sections/pricing/pricing-plan';
import { DataHelper } from 'src/services/dataHelper';
import { useNavigate } from 'react-router-dom';
import { paths } from 'src/paths';
import Loader from 'src/pages/components/loader';
import { useTranslation } from 'react-i18next';
import { useCart } from 'src/hooks/use-carts';

const Page = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0); // 0-based page index
  const [rowsPerPage, setRowsPerPage] = useState(16); // increment size
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searching, setSearching] = useState('');
  const [searchData, setSearchData] = useState([]);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const {t}=useTranslation();
  const { refreshCartCount } = useCart();

  // Fetch items with pagination
  const fetchData = async (pageNumber, pageSize) => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const res = await new DataHelper().getListPos('Items', pageSize, pageNumber + 1);
      const newRows = res?.data?.list || [];
      if (newRows.length > 0) {
        setRows(prev => [...prev, ...newRows]);
        setSearchData(prev => [...prev, ...newRows]);
        setHasMore(newRows.length === pageSize); // if less than pageSize, no more pages
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error(error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchData(page, rowsPerPage);
    refreshCartCount();
  }, []);

  // Infinite scroll
  const handleScroll = () => {
    if (!containerRef.current || loading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchData(nextPage, rowsPerPage);
    }
  };

  useEffect(() => {
    const current = containerRef.current;
    if (current) {
      current.addEventListener('scroll', handleScroll);
      return () => current.removeEventListener('scroll', handleScroll);
    }
  }, [rows, loading, hasMore]);

  // Local search
  const filterFunction = (text) => {
    const filter = searchData.filter(item =>
      item.itemName?.toLowerCase().includes(text.toLowerCase()) ||
      item.categoryName?.toLowerCase().includes(text.toLowerCase()) ||
      item.unitName?.toLowerCase().includes(text.toLowerCase()) ||
      item.code === text
    );
    setRows(filter);
    setSearching(text);
  };

  const onPress = (id) => {
    navigate(paths.inv.product.createProduct, { state: { ID: id } });
  };

  return (
    <>
      <Seo title="Products" />
      <Box component="main" sx={{ flexGrow: 1, py: 1 }}>
        <Container maxWidth="xl">
          <Stack spacing={2}>
            {/* SEARCH */}
            <Stack direction="row" spacing={1}>
              <TextField
                placeholder={t('Search here')}
                fullWidth
                variant="outlined"
                value={searching}
                onChange={(e) => filterFunction(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SvgIcon>
                      <SearchMdIcon />
                    </SvgIcon>
                  )
                }}
              />
            </Stack>

            {/* PRODUCTS GRID */}
            <Box
              ref={containerRef}
              sx={{
                borderRadius: 2.5,
                maxHeight: '80vh',
                overflowY: 'auto',
                mt: 2
              }}
            >
              {rows.length === 0 && !loading ? (
                <Typography align="center" sx={{ mt: 5 }}>
                  No products found.
                </Typography>
              ) : (
                <Grid container spacing={3} paddingLeft={3} paddingTop={2}>
                  {rows.map((row, index) =>
                    row.isDisplay ? (
                      <Grid xs={12} md={3} padding={1} key={index}>
                        <PricingPlan
                          cta={t('View more')}
                          currency={t('AED')}
                          description={row.remarks || 'Description.'}
                          image={row.image}
                          name={row.itemName}
                          popular
                          price={row.salePrice?.toFixed(2)}
                          onClick={() => onPress(row.ID)}
                          sx={{ height: '100%', maxWidth: 460, mx: 'auto' }}
                        />
                      </Grid>
                    ) : null
                  )}
                </Grid>
              )}

              {/* LOADING */}
              {loading && (
                
                <Loader />
              )}

              {!hasMore && rows.length > 0 && (
                <Typography align="center" sx={{ mt: 2 }}>
                  No more products
                </Typography>
              )}
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Page;
